import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Routes, Route, Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Register from './pages/Register';
import LedgerTransactions from './pages/LedgerTransactions';
import Categories from './pages/Categories';
import CreditCards from './pages/CreditCards';
import Layout from './components/Layout';
import DataImport from './pages/DataImport';
import DataExport from './pages/DataExport';
import RecurringTransactions from './pages/RecurringTransactions';
import ErrorPage from './pages/ErrorPage';
import Profile from './pages/Profile';
import TransactionDashboard from './pages/TransactionDashboard';
import CashFlow from './pages/CashFlow';
import Settings from './pages/Settings';
import AdminScripts from './pages/AdminScripts';
import AdminQuota from './pages/AdminQuota';
import AdminLogs from './pages/AdminLogs';
import AdminOverview from './pages/AdminOverview';
import AdminDemoSeed from './pages/AdminDemoSeed';
import AdminLayout from './components/AdminLayout';
import AdminSymbolSync from './components/AdminSymbolSync';
import Budget from './pages/Budget';
import Advisor from './pages/Advisor';

import Documents from './pages/Documents';
import { FormField, filledInputSx, arborGreen } from './components/FormDialog';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import config from './config/config';
import PVLogo from './components/PVLogo';
import { useTranslation } from 'react-i18next';
import { LanguageMenuButton } from './components/LanguageSelector';

const Investments = lazy(() => import('./pages/Investments'));

function LazyInvestments({ activeSubView }) {
  return (
    <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress /></Box>}>
      <Investments activeSubView={activeSubView} />
    </Suspense>
  );
}

function RootRedirector() {
  const { t } = useTranslation();
  const { api, setCurrentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchAndRedirect = async () => {
      try {
        const [userRes, ledgersRes] = await Promise.all([
          api.get('/users/me'),
          api.get('/ledger/ledgers')
        ]);
        if (!isMounted) return;

        const userData = userRes.data;
        const ledgers = ledgersRes.data;

        if (setCurrentUser && userData) {
          setCurrentUser(prev => ({ ...prev, ...userData }));
        }

        if (ledgers && ledgers.length > 0) {
          const targetLedgerId = userData?.last_ledger_id;
          const exists = targetLedgerId && ledgers.find(l => l.id.toString() === targetLedgerId.toString());
          if (exists) {
            navigate(`/ledger/${targetLedgerId}/dashboard`, { replace: true });
          } else {
            navigate(`/ledger/${ledgers[0].id}/dashboard`, { replace: true });
          }
        }
      } catch (err) {
        console.error("Failed to fetch ledgers for redirect", err);
      }
    };
    fetchAndRedirect();
    return () => {
      isMounted = false;
    };
  }, [api, navigate, setCurrentUser]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress size={24} sx={{ color: arborGreen, mr: 2 }} />
      <Typography color="text.secondary" sx={{ fontWeight: 500 }}>{t('Loading books...')}</Typography>
    </Box>
  );
}

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, token, setToken: updateToken } = useAuth();
  const passwordRef = useRef(null);

  // 2FA state
  const [twoFaRequired, setTwoFaRequired] = useState(false);
  const [twoFaTempToken, setTwoFaTempToken] = useState('');
  const [twoFaMethods, setTwoFaMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear inputs when the component mounts
  useEffect(() => {
    setEmail('');
    setPassword('');
    setError('');
    setTwoFaRequired(false);
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result === true) {
        setEmail('');
        setPassword('');
      } else if (result && result.requires_2fa) {
        setTwoFaRequired(true);
        setTwoFaTempToken(result.temp_token);
        setTwoFaMethods(result.methods);
        if (result.methods.length === 1) {
          setSelectedMethod(result.methods[0]);
          if (result.methods[0] === 'email') {
            setEmailSent(true);
          }
        }
      } else {
        setError('Invalid email or password. Please try again.');
        setPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMethod = async (method) => {
    setSelectedMethod(method);
    setError('');
    if (method === 'email') {
      try {
        await axios.post(`${config.API_URL}/auth/send-2fa-email`, {
          temp_token: twoFaTempToken
        });
        setEmailSent(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to send verification email');
      }
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setTwoFaLoading(true);
    setError('');
    try {
      const response = await axios.post(`${config.API_URL}/auth/verify-2fa`, {
        temp_token: twoFaTempToken,
        code: twoFaCode,
        method: selectedMethod
      });
      updateToken(response.data.access_token);
      setEmail('');
      setPassword('');
      setTwoFaRequired(false);
      setTwoFaCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
      setTwoFaCode('');
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleBack = () => {
    setTwoFaRequired(false);
    setSelectedMethod('');
    setTwoFaCode('');
    setTwoFaTempToken('');
    setTwoFaMethods([]);
    setEmailSent(false);
    setError('');
  };

  if (token) {
    return (
      <Layout>
        <RootRedirector />
      </Layout>
    );
  }

  // 2FA Method Picker (shown when both methods are enabled)
  if (twoFaRequired && !selectedMethod && twoFaMethods.length > 1) {
    return (
      <Container component="main" maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: { xs: 3, sm: 5 }, width: '100%', border: '1px solid', borderColor: 'divider' }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Choose a verification method
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>}
          <Stack spacing={2} sx={{ width: '100%' }}>
            {twoFaMethods.includes('email') && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<EmailIcon />}
                onClick={() => handleSelectMethod('email')}
                sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Email Verification</Typography>
                  <Typography variant="caption" color="text.secondary">Send a code to your email</Typography>
                </Box>
              </Button>
            )}
            {twoFaMethods.includes('totp') && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<PhonelinkLockIcon />}
                onClick={() => handleSelectMethod('totp')}
                sx={{ py: 2, justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>Authenticator App</Typography>
                  <Typography variant="caption" color="text.secondary">Use your authenticator app</Typography>
                </Box>
              </Button>
            )}
          </Stack>
          <Button onClick={handleBack} sx={{ mt: 3 }} startIcon={<ArrowBackIcon />}>
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  // 2FA Code Entry
  if (twoFaRequired && selectedMethod) {
    return (
      <Container component="main" maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: { xs: 3, sm: 5 }, width: '100%', border: '1px solid', borderColor: 'divider' }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Enter Verification Code
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {selectedMethod === 'email'
              ? (emailSent ? 'A verification code has been sent to your email.' : 'Sending code...')
              : 'Enter the code from your authenticator app.'
            }
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2, width: '100%' }}>{error}</Alert>}
          <Box component="form" onSubmit={handleVerify2fa} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Verification Code"
              value={twoFaCode}
              onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputProps={{ maxLength: 6, style: { letterSpacing: 8, textAlign: 'center', fontSize: 24 }, inputMode: 'numeric' }}
              autoFocus
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disableElevation
              disabled={twoFaLoading || twoFaCode.length !== 6}
              startIcon={twoFaLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                mb: 2,
                '&.Mui-disabled': {
                  bgcolor: (theme) => twoFaCode.length === 6 ? arborGreen : theme.palette.action.disabledBackground,
                  color: (theme) => twoFaCode.length === 6 ? '#ffffff' : theme.palette.action.disabled,
                  opacity: twoFaLoading ? 0.85 : 1,
                }
              }}
            >
              {twoFaLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button onClick={handleBack} startIcon={<ArrowBackIcon />} size="small">
                Back
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Normal Login Form
  return (
    <Container component="main" maxWidth="xs" sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 1100 }}>
        <LanguageMenuButton />
      </Box>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 4, sm: 6 },
          width: '100%',
          borderRadius: 4, // 32px roughly
          boxShadow: '0 40px 100px rgba(10, 42, 33, 0.08)',
          border: 'none'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 4 }}>
          <PVLogo sx={{ width: 64, height: 64 }} />
          <Typography component="h1" variant="h4" noWrap sx={{ fontWeight: 800, color: '#006D43', letterSpacing: '-0.04em' }}>
            PVBooks
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontWeight: 500, textAlign: 'center' }}>
          {t('Sign in to your account')}
        </Typography>

        <Box component="form" onSubmit={handleLogin} noValidate sx={{ width: '100%' }}>
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

          <Stack spacing={3}>
            <FormField label={t('Email Address')} id="email" required>
              <TextField
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
                variant="filled"
                placeholder="Enter your email"
                sx={filledInputSx}
                InputProps={{ disableUnderline: true }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    passwordRef.current?.focus();
                  }
                }}
              />
            </FormField>

            <FormField label={t('Password')} id="password" required>
              <TextField
                inputRef={passwordRef}
                fullWidth
                name="password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                variant="filled"
                sx={filledInputSx}
                InputProps={{ disableUnderline: true }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin(e);
                  }
                }}
              />
            </FormField>
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disableElevation
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              mt: 5,
              mb: 3,
              py: 2,
              fontSize: '1rem',
              fontWeight: 700,
              bgcolor: arborGreen,
              color: '#ffffff',
              '&:hover': { bgcolor: '#061a15' },
              '&.Mui-disabled': {
                bgcolor: arborGreen,
                color: '#ffffff',
                opacity: 0.85,
              }
            }}
          >
            {loading ? t('Loading...') : t('Sign In')}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Link component={RouterLink} to="/register" variant="body2" sx={{ color: arborGreen, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              {t("Don't have an account? Sign up")}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/ledger/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <LedgerTransactions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/recurring"
        element={
          <ProtectedRoute>
            <Layout>
              <RecurringTransactions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/cashflow"
        element={
          <ProtectedRoute>
            <Layout>
              <CashFlow />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/budget"
        element={
          <ProtectedRoute>
            <Layout>
              <Budget />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="dashboard" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="dashboard" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments/holdings"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="holdings" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments/tax"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="tax" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments/realized-gains"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="tax" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/investments/trades"
        element={
          <ProtectedRoute>
            <Layout>
              <LazyInvestments activeSubView="trades" />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ledger/:id/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <TransactionDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ledger/:id/advisor"
        element={
          <ProtectedRoute>
            <Layout>
              <Advisor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Layout>
              <Categories />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/credit-cards"
        element={
          <ProtectedRoute>
            <Layout>
              <CreditCards />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminOverview />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/demo-data"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDemoSeed />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-demo"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDemoSeed />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/quota"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminQuota />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-quota"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminQuota />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scripts"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminScripts />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-scripts"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminScripts />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminLogs />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/securities"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminSymbolSync />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin-logs"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminLogs />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <DataImport />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <DataImport />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/export"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <DataExport />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <DataExport />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <Documents />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
}

export default App;